/**
 * useStepEditor — Custom React hook for step state management.
 *
 * Features:
 *  - Steps state with immutable updates
 *  - Undo history (configurable depth)
 *  - Dirty tracking (compares against last-saved snapshot)
 *  - API integration: load from GET /extension-scripts/:id, save via PUT
 *  - Step CRUD helpers (add, delete, move, duplicate, reorder via drag)
 *  - Execution status per step (pending / running / passed / failed)
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import extensionScriptsService from '../../services/api/extensionScriptsService';
import { createStepFromTemplate } from './stepTypes';

const MAX_UNDO = 30;

const renumber = (arr) => arr.map((s, i) => ({ ...s, stepNumber: i + 1 }));

const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

export default function useStepEditor(scriptId) {
  // ---- Core state ----
  const [steps, setStepsRaw] = useState([]);
  const [script, setScript] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // ---- Undo stack ----
  const undoStack = useRef([]);
  const [canUndo, setCanUndo] = useState(false);

  // ---- Dirty tracking ----
  const savedSnapshot = useRef('[]');
  const [isDirty, setIsDirty] = useState(false);

  // ---- Expanded step ----
  const [expandedIndex, setExpandedIndex] = useState(null);

  // ---- Execution status per step ----
  const [stepStatuses, setStepStatuses] = useState({});

  // Push current steps onto undo stack, then apply new steps
  const pushUndo = useCallback((currentSteps) => {
    undoStack.current = [...undoStack.current.slice(-(MAX_UNDO - 1)), deepClone(currentSteps)];
    setCanUndo(true);
  }, []);

  const setSteps = useCallback(
    (nextSteps) => {
      setStepsRaw((prev) => {
        pushUndo(prev);
        const numbered = renumber(nextSteps);
        setIsDirty(JSON.stringify(numbered) !== savedSnapshot.current);
        return numbered;
      });
    },
    [pushUndo]
  );

  // ---- Undo ----
  const undo = useCallback(() => {
    if (undoStack.current.length === 0) return;
    const prev = undoStack.current.pop();
    setCanUndo(undoStack.current.length > 0);
    setStepsRaw(prev);
    setIsDirty(JSON.stringify(prev) !== savedSnapshot.current);
  }, []);

  // ---- Discard changes (revert to saved) ----
  const discard = useCallback(() => {
    const saved = JSON.parse(savedSnapshot.current);
    setStepsRaw(saved);
    undoStack.current = [];
    setCanUndo(false);
    setIsDirty(false);
    setExpandedIndex(null);
  }, []);

  // ---- Load script from API ----
  const loadScript = useCallback(
    async (id) => {
      const targetId = id || scriptId;
      if (!targetId) return;
      setLoading(true);
      setError(null);
      try {
        const { data } = await extensionScriptsService.getOne(targetId);
        const s = data;
        setScript(s);
        const loadedSteps = renumber(s.steps || []);
        setStepsRaw(loadedSteps);
        savedSnapshot.current = JSON.stringify(loadedSteps);
        undoStack.current = [];
        setCanUndo(false);
        setIsDirty(false);
        setExpandedIndex(null);
        setStepStatuses({});
      } catch (e) {
        setError(e.response?.data?.message || e.message || 'Failed to load script');
      } finally {
        setLoading(false);
      }
    },
    [scriptId]
  );

  // ---- Save script to API ----
  const saveScript = useCallback(async () => {
    if (!script?.id) return;
    setSaving(true);
    setError(null);
    try {
      // Only send fields the UpdateExtensionScriptDto allows
      const payload = {
        name: script.name,
        type: script.type,
        urlPatterns: script.urlPatterns,
        script: script.script,
        scriptFormat: script.scriptFormat,
        steps,
        isActive: script.isActive,
        isDefault: script.isDefault,
        credentialPlaceholders: script.credentialPlaceholders,
        ssoFlow: script.ssoFlow,
        notes: script.notes,
        changelog: script.changelog,
        priority: script.priority,
        appKey: script.appKey,
        scriptType: script.scriptType,
        appConfigKey: script.appConfigKey,
        // firePolicy: nullable JSONB — explicit null clears stored policy and
        // makes the runtime apply all defaults. Pass through `undefined` to
        // skip; it gets stripped below.
        firePolicy: script.firePolicy === undefined ? undefined : script.firePolicy,
      };
      // Strip undefined values so we don't send nulls for fields the script doesn't have.
      // (firePolicy: null is intentional and must NOT be stripped.)
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
      await extensionScriptsService.update(script.id, payload);
      savedSnapshot.current = JSON.stringify(steps);
      undoStack.current = [];
      setCanUndo(false);
      setIsDirty(false);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to save script');
    } finally {
      setSaving(false);
    }
  }, [script, steps]);

  // ---- Step CRUD helpers ----

  const addStep = useCallback(
    (templateKey) => {
      const step = createStepFromTemplate(templateKey, steps.length + 1);
      if (!step) return;
      setSteps([...steps, step]);
      setExpandedIndex(steps.length); // expand the new step
    },
    [steps, setSteps]
  );

  const addStepObj = useCallback(
    (stepObj) => {
      setSteps([...steps, stepObj]);
      setExpandedIndex(steps.length);
    },
    [steps, setSteps]
  );

  /**
   * Insert a batch of steps at a position derived from afterIndex (the playhead).
   *  - afterIndex = null / undefined / < 0  → insert at position 0 (beginning)
   *  - afterIndex = N                      → insert at position N+1 (just after step N)
   * After insertion, the playhead advances to the last inserted step so that
   * subsequent recorded steps continue appending after the new group.
   */
  const insertStepsBatch = useCallback(
    (stepObjs, afterIndex) => {
      if (!stepObjs || stepObjs.length === 0) return;
      const base = (afterIndex == null || afterIndex < 0) ? 0 : afterIndex + 1;
      setStepsRaw((prev) => {
        pushUndo(prev);
        const next = [...prev];
        next.splice(base, 0, ...stepObjs);
        const numbered = renumber(next);
        setIsDirty(JSON.stringify(numbered) !== savedSnapshot.current);
        return numbered;
      });
      setExpandedIndex(base + stepObjs.length - 1);
    },
    [pushUndo]
  );

  const updateStep = useCallback(
    (index, updatedStep) => {
      const next = [...steps];
      next[index] = updatedStep;
      setSteps(next);
    },
    [steps, setSteps]
  );

  const deleteStep = useCallback(
    (index) => {
      setSteps(steps.filter((_, i) => i !== index));
      if (expandedIndex === index) setExpandedIndex(null);
      else if (expandedIndex !== null && expandedIndex > index) {
        setExpandedIndex(expandedIndex - 1);
      }
    },
    [steps, setSteps, expandedIndex]
  );

  const duplicateStep = useCallback(
    (index) => {
      const clone = deepClone(steps[index]);
      clone.name = (clone.name || '') + ' (copy)';
      const next = [...steps];
      next.splice(index + 1, 0, clone);
      setSteps(next);
      setExpandedIndex(index + 1);
    },
    [steps, setSteps]
  );

  const moveStep = useCallback(
    (index, direction) => {
      const target = index + direction;
      if (target < 0 || target >= steps.length) return;
      const next = [...steps];
      [next[index], next[target]] = [next[target], next[index]];
      setSteps(next);
      if (expandedIndex === index) setExpandedIndex(target);
      else if (expandedIndex === target) setExpandedIndex(index);
    },
    [steps, setSteps, expandedIndex]
  );

  const reorderStep = useCallback(
    (fromIndex, toIndex) => {
      if (fromIndex === toIndex) return;
      const next = [...steps];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      setSteps(next);
      if (expandedIndex === fromIndex) setExpandedIndex(toIndex);
    },
    [steps, setSteps, expandedIndex]
  );

  // ---- Step status management (for execution) ----

  const setStepStatus = useCallback((stepIndex, status) => {
    setStepStatuses((prev) => ({ ...prev, [stepIndex]: status }));
  }, []);

  const resetStatuses = useCallback(() => {
    setStepStatuses({});
  }, []);

  // ---- Auto-load on mount if scriptId provided ----
  useEffect(() => {
    if (scriptId) {
      loadScript(scriptId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptId]);

  return {
    // State
    steps,
    script,
    loading,
    saving,
    error,
    isDirty,
    canUndo,
    expandedIndex,
    stepStatuses,

    // Setters
    setSteps,
    setExpandedIndex,
    setError,

    // Actions
    loadScript,
    saveScript,
    undo,
    discard,

    // Step CRUD
    addStep,
    addStepObj,
    insertStepsBatch,
    updateStep,
    deleteStep,
    duplicateStep,
    moveStep,
    reorderStep,

    // Execution status
    setStepStatus,
    resetStatuses,
  };
}
